import _ from 'lodash';
import { sqDurationStore, sqTrendScalarStore, sqTrendStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { CANCELLATION_GROUP_GUID_SEPARATOR, cancelGroup } from '@/requests/pendingRequests.utilities';
import { getAlignableItems, getItemId } from '@/trend/trendDataHelper.utilities';
import {
  catchItemDataFailure,
  fetchItemProps,
  setCustomizationProps,
  setTrendItemProps,
} from '@/trendData/trend.actions';
import { getLaneForReservation, reindexOrderInLaneForReservation } from '@/trendData/trendLaneReservation.utilities';
import { updateLaneDisplay } from '@/trendData/yAxis.actions';
import { computeScalar } from '@/utilities/formula.utilities';
import { isItemRedacted } from '@/utilities/redaction.utilities';
import { isHidden } from '@/utilities/trendChartItemsHelper.utilities';
import type { PushOption } from '@/core/flux.service';
import type { BaseItem, ScalarItem } from '@/trendData/trendData.types';

export function setScalarDataStatusLoading(id: string, ...option: [PushOption?]): void {
  flux.dispatch('TREND_SET_DATA_STATUS_LOADING', { id }, ...option);
}

export function setScalarDataStatusHiddenFromTrend(id: string, ...option: [PushOption?]): void {
  flux.dispatch('TREND_SET_DATA_STATUS_HIDDEN_FROM_TREND', { id }, ...option);
}

/**
 * Adds a scalar item and fetches the data for it.
 *
 * @param {Object} item - A scalar item from the REST API.
 * @param {Object} [props] - additional properties to be set on the item before fetching
 * @param {String} [option] - One of the WORKSTEP_PUSH constants
 * @return {Object} A promise that will be fulfilled when it is finished fetching.
 */
export function addScalar(item: ScalarItem, props?: object, option?: PushOption) {
  if (!sqTrendScalarStore.findItem(item.id)) {
    const reservation = item.laneReservation
      ? getLaneForReservation(
          item.laneReservation,
          item.laneReservationOptions,
          setCustomizationProps,
          () => getAlignableItems({}) as BaseItem[],
        )
      : undefined;

    const laneAndAlignmentExist = (reservation || item.lane) && (reservation?.axisAlign || item.axisAlign);
    const payload = {
      id: item.id,
      name: item.name,
      isArchived: !!item.isArchived,
      lane: reservation?.lane ?? (item.lane ? item.lane : sqTrendStore.getNextLane()),
      alignment: reservation?.axisAlign ?? (item.axisAlign ? item.axisAlign : sqTrendStore.nextAlignment),
      color: item.color,
      effectivePermissions: item.effectivePermissions,
      laneReservation: item.laneReservation,
    };

    // If we are adding a scalar to a chart with only one lane and alignment displaying, add it to the displayed ones
    const uniqueLanes = sqTrendStore.uniqueLanes;
    const uniqueAlignments = sqTrendStore.uniqueAlignments;
    if (!laneAndAlignmentExist && uniqueLanes.length === 1 && uniqueAlignments.length === 1) {
      payload.lane = uniqueLanes[0];
      payload.alignment = uniqueAlignments[0];
    }

    flux.dispatch('TREND_ADD_SCALAR', payload, option);

    if (item.laneReservation) {
      reindexOrderInLaneForReservation(
        item.laneReservation,
        setCustomizationProps,
        () => getAlignableItems({}) as BaseItem[],
      );
    }
  }

  setTrendItemProps(item.id, props, option);

  return Promise.all([fetchItemProps(item.id), fetchScalar(item.id)]);
}

export function fetchAllScalars(skipChildren?: boolean) {
  return _.chain(sqTrendScalarStore.items)
    .reject((item) => isItemRedacted(item))
    .reject((item: any) => skipChildren && item.isChildOf)
    .map((item: any) => fetchScalar(item.id))
    .thru((promises) => Promise.all(promises))
    .value();
}

export function fetchScalar(itemId: string) {
  const item = sqTrendScalarStore.findItem(itemId);
  const cancellationGroup = `fetchScalar${CANCELLATION_GROUP_GUID_SEPARATOR}${itemId}`;

  if (isHidden({ item })) {
    setScalarDataStatusHiddenFromTrend(item.id, PUSH_IGNORE);
    return Promise.resolve();
  }

  setScalarDataStatusLoading(item.id, PUSH_IGNORE);
  cancelGroup(cancellationGroup, true);

  let scalarRequest;
  if (item.shadedAreaUpper && item.shadedAreaLower) {
    scalarRequest = Promise.all([
      computeScalar({ id: item.shadedAreaLower.id, cancellationGroup }),
      computeScalar({ id: item.shadedAreaUpper.id, cancellationGroup }),
    ]).then(([lowerResult, upperResult]) => {
      return _.assign({}, _.omit(lowerResult, ['value']), {
        lower: lowerResult.value,
        upper: upperResult.value,
      });
    });
  } else {
    scalarRequest = computeScalar({ id: getItemId(item), cancellationGroup });
  }
  return scalarRequest
    .then((results) => {
      const { start, end } = sqDurationStore.displayRange;
      const payload = _.assign(
        { id: item.id, start: start.valueOf(), end: end.valueOf() },
        _.pick(results, [
          'value',
          'lower',
          'upper',
          'warningCount',
          'warningLogs',
          'timingInformation',
          'meterInformation',
        ]),
      );
      flux.dispatch('TREND_SCALAR_RESULTS_SUCCESS', payload, PUSH_IGNORE);
      updateLaneDisplay();
      return payload;
    })
    .catch(_.partial(catchItemDataFailure, item.id, cancellationGroup));
}
